import { useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { Mail, X } from "lucide-react";
import { toast } from "sonner";

/**
 * Persistent banner for authenticated email-auth users whose email isn't verified.
 * - Hidden for: guests, Google users, already-verified users.
 * - Resend button calls POST /auth/resend-verification (60s server-side throttle).
 * - Session-scoped dismiss (sessionStorage) so it reappears next visit.
 */
export default function VerifyEmailBanner() {
  const { user, refreshUser } = useAuth();
  const [dismissed, setDismissed] = useState(
    typeof window !== "undefined" && sessionStorage.getItem("yumo_dismiss_verify") === "1"
  );
  const [sending, setSending] = useState(false);

  if (!user) return null;
  if (user.auth_provider !== "email") return null;
  if (user.email_verified) return null;
  if (dismissed) return null;

  const handleResend = async () => {
    setSending(true);
    try {
      const { data } = await api.post("/auth/resend-verification");
      if (data?.already_verified) {
        toast.success("Already verified — refreshing…");
        try { await refreshUser(); } catch (e) { console.warn("[refreshUser] failed", e?.message); }
      } else {
        toast.success("Verification email sent. Check your inbox.");
      }
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Couldn't resend right now.");
    } finally {
      setSending(false);
    }
  };

  const handleDismiss = () => {
    sessionStorage.setItem("yumo_dismiss_verify", "1");
    setDismissed(true);
  };

  return (
    <div
      className="w-full bg-primary/10 border-b border-primary/20 text-foreground"
      data-testid="verify-email-banner"
      role="status"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-1.5 md:py-2.5 flex items-center gap-2 md:gap-3 flex-wrap">
        <Mail className="w-4 h-4 text-primary shrink-0" strokeWidth={1.75} />
        <div className="text-xs md:text-sm flex-1 min-w-[160px]">
          <span className="font-medium">Verify your email</span>
          <span className="hidden md:inline text-muted-foreground"> — we sent a link to <span className="text-foreground">{user.email}</span>.</span>
        </div>
        <button
          onClick={handleResend}
          disabled={sending}
          className="text-xs md:text-sm font-medium text-primary hover:underline disabled:opacity-60"
          data-testid="verify-resend-btn"
        >
          {sending ? "Sending…" : "Resend"}
        </button>
        <button
          onClick={handleDismiss}
          className="text-muted-foreground hover:text-foreground transition shrink-0 ml-1"
          aria-label="Dismiss"
          data-testid="verify-dismiss-btn"
        >
          <X className="w-4 h-4" strokeWidth={1.75} />
        </button>
      </div>
    </div>
  );
}
